import {
  ComponentProps,
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { clsx } from 'clsx'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

import { CarouselArrow } from './CarouselArrow.js'
import { useEmblaCarousel } from './useEmblaCarousel.js'
import { preventEdgeScrolling } from './utils.js'

const wheelGesturesPluginInstance = WheelGesturesPlugin()

export declare type CarouselItemType = ReactElement<{
  /**
    Required. id for every item, should be unique
   */
  itemId: string
}>

type CarouselProps = ComponentProps<'div'> & {
  items?: CarouselItemType[]
  slidesToScroll?: number | 'auto'
}

export const Carousel = ({
  className,
  items = [],
  slidesToScroll = 'auto',
}: CarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      slidesToScroll,
      containScroll: 'keepSnaps',
    },
    [wheelGesturesPluginInstance],
  )

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)

  const scrollPrev = useCallback(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return
    }
    setPrevBtnEnabled(emblaApi.canScrollPrev())
    setNextBtnEnabled(emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.on('select', onSelect)
    onSelect()
  }, [emblaApi, onSelect])

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.on('scroll', preventEdgeScrolling(emblaApi))
  }, [emblaApi])

  return (
    <div className="relative">
      <div ref={emblaRef} className="overflow-hidden">
        <div
          className={clsx(
            'flex items-stretch snap-x h-auto space-s-2 px-2 sm:px-0',
            className,
          )}
        >
          {items.map(item => (
            <div
              className="snap-start grow-0 shrink-0 min-w-0 flex"
              key={item?.key}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <CarouselArrow
        onClick={scrollPrev}
        disabled={!prevBtnEnabled}
        direction="left"
        label="Previous"
      />
      <CarouselArrow
        onClick={scrollNext}
        disabled={!nextBtnEnabled}
        direction="right"
        label="Next"
      />
    </div>
  )
}
